import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import type { DashboardInvoiceActionItem } from '@/lib/api/dashboard';
import { updateInvoiceStatus, resendInvoiceEmail } from '@/lib/api/invoices';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import InvoiceStatusConfirmDialog from '@/components/dashboard/InvoiceStatusConfirmDialog';
import type { InvoiceDialogFeedback } from '@/components/dashboard/InvoiceConfirmDialog';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { Tag } from '@/components/ui/tag';

import {
  CS_PRIMARY,
  DASHBOARD_TODAY_CARD_EMPTY_MIN_HEIGHT,
  DASHBOARD_TODAY_EMPTY_STATE_CONTAINER_SX,
  DASHBOARD_TODAY_EMPTY_STATE_TEXT_SX,
} from './dashboardHomeTokens';
import { DashboardCardFooterLink } from './DashboardCardFooterLink';
import {
  formatDashboardCurrency,
  formatDashboardDueLabel,
  formatDashboardDateLabel,
  parseDashboardDate,
  toDashboardTitle,
} from './dashboardHomeFormatters';

/** Same paths as `settings/invoice-tax-details.svg`; blue (`CS_PRIMARY`) for dashboard card header only. */
const InvoiceTaxDetailsIconBlue = () => (
  <Box
    component="svg"
    width={26}
    height={26}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    sx={{ display: 'block' }}
  >
    <path
      opacity={0.5}
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
      fill={CS_PRIMARY}
    />
    <path
      d="M12.75 6C12.75 5.58579 12.4142 5.25 12 5.25C11.5858 5.25 11.25 5.58579 11.25 6V6.31673C9.61957 6.60867 8.25 7.83361 8.25 9.5C8.25 11.4172 10.0628 12.75 12 12.75C13.3765 12.75 14.25 13.6557 14.25 14.5C14.25 15.3443 13.3765 16.25 12 16.25C10.6235 16.25 9.75 15.3443 9.75 14.5C9.75 14.0858 9.41421 13.75 9 13.75C8.58579 13.75 8.25 14.0858 8.25 14.5C8.25 16.1664 9.61957 17.3913 11.25 17.6833V18C11.25 18.4142 11.5858 18.75 12 18.75C12.4142 18.75 12.75 18.4142 12.75 18V17.6833C14.3804 17.3913 15.75 16.1664 15.75 14.5C15.75 12.5828 13.9372 11.25 12 11.25C10.6235 11.25 9.75 10.3443 9.75 9.5C9.75 8.65573 10.6235 7.75 12 7.75C13.3765 7.75 14.25 8.65573 14.25 9.5C14.25 9.91421 14.5858 10.25 15 10.25C15.4142 10.25 15.75 9.91421 15.75 9.5C15.75 7.83361 14.3804 6.60867 12.75 6.31673V6Z"
      fill={CS_PRIMARY}
    />
  </Box>
);

const ClockCircleIconRed = () => (
  <Box
    component="svg"
    width={16}
    height={16}
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    sx={{ display: 'block' }}
  >
    <path
      opacity={0.5}
      d="M10.9095 20.008C15.9323 20.008 20.0041 15.9362 20.0041 10.9134C20.0041 5.89062 15.9323 1.81885 10.9095 1.81885C5.88671 1.81885 1.81494 5.89062 1.81494 10.9134C1.81494 15.9362 5.88671 20.008 10.9095 20.008Z"
      fill="#ea5455"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.9135 6.59375C11.2902 6.59375 11.5956 6.89913 11.5956 7.27584V10.6311L13.6695 12.705C13.9359 12.9714 13.9359 13.4032 13.6695 13.6696C13.4031 13.936 12.9712 13.936 12.7049 13.6696L10.4312 11.396C10.3033 11.2681 10.2314 11.0946 10.2314 10.9137V7.27584C10.2314 6.89913 10.5368 6.59375 10.9135 6.59375Z"
      fill="#ea5455"
    />
  </Box>
);

export interface DashboardInvoicesBillingCardProps {
  items?: DashboardInvoiceActionItem[];
  isLoading?: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onViewAll?: () => void;
  /** Called after a successful invoice status PATCH so the parent can refetch dashboard data. */
  onInvoiceDataChanged?: () => void | Promise<void>;
}

/** Same values as `InvoicingPage` status column. */
type InvoiceStatusSelectValue = 'due' | 'overdue' | 'paid' | 'discarded';

const LIST_MARK_PAID_PAYMENT_METHOD = 'upi';

const mapUiStatusToApiStatus = (value: InvoiceStatusSelectValue): string => (value === 'due' ? 'sent' : value);

const normalizeDashboardSelectValue = (item: DashboardInvoiceActionItem): InvoiceStatusSelectValue => {
  const raw = (item.invoiceStatus || '').toLowerCase().trim();
  if (raw === 'paid') return 'paid';
  if (raw === 'discarded' || raw === 'archived') return 'discarded';
  if (raw === 'overdue') return 'overdue';
  if (raw === 'sent' || raw === 'due' || raw === 'unpaid' || raw === '') {
    return item.status === 'overdue' ? 'overdue' : 'due';
  }
  return item.status === 'overdue' ? 'overdue' : 'due';
};

const statusSelectLabel = (value: InvoiceStatusSelectValue): string => {
  if (value === 'overdue') return 'Overdue';
  if (value === 'paid') return 'Paid';
  if (value === 'discarded') return 'Discarded';
  return 'Due';
};

const statusSelectColor = (value: InvoiceStatusSelectValue): string => {
  if (value === 'paid') return '#28c76f';
  if (value === 'overdue') return '#ea5455';
  if (value === 'discarded') return '#64748b';
  return '#ea5455';
};

const sortInvoiceItems = (items: DashboardInvoiceActionItem[]) =>
  [...items].sort((left, right) => {
    const leftStatus = left.status === 'overdue' ? 0 : 1;
    const rightStatus = right.status === 'overdue' ? 0 : 1;
    if (leftStatus !== rightStatus) return leftStatus - rightStatus;
    const leftDue = parseDashboardDate(left.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const rightDue = parseDashboardDate(right.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return leftDue - rightDue;
  });

/** Dashboard API may send action codes, not user-facing copy — hide those as plain text. */
const MACHINE_RECOMMENDED_ACTION_KEYS = new Set(['send_reminder', 'review_invoice']);

const getHumanRecommendedAction = (raw?: string | null): string | null => {
  const t = raw?.trim();
  if (!t) return null;
  if (MACHINE_RECOMMENDED_ACTION_KEYS.has(t.toLowerCase())) return null;
  return t;
};

const invoiceHeroPaperSx = {
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
} as const;

const heroCollapseIconSx = {
  position: 'absolute' as const,
  top: 8,
  right: 8,
  zIndex: 1,
  width: 30,
  height: 30,
  padding: 0,
  border: '1px solid rgba(148, 163, 184, 0.35)',
  bgcolor: 'rgba(255, 255, 255, 0.96)',
  '&:hover': { bgcolor: '#f8fafc', borderColor: 'rgba(148, 163, 184, 0.55)' },
};

const chipSxForStatus = (overdue: boolean) =>
  ({
    height: 22,
    fontWeight: overdue ? 700 : 600,
    fontSize: 12,
    ...(overdue
      ? { bgcolor: '#fff1f2', color: '#dc2626', borderColor: '#fecdd3' }
      : { bgcolor: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }),
  }) as const;

const chipClassNameForStatus = (overdue: boolean): string =>
  overdue
    ? 'font-bold !border-[#fecdd3] !bg-[#fff1f2] !text-[#dc2626]'
    : 'font-semibold !border-[#fed7aa] !bg-[#fff7ed] !text-[#c2410c]';

const DASHBOARD_INVOICE_STATUS_OPTIONS: InvoiceStatusSelectValue[] = ['due', 'overdue', 'paid', 'discarded'];

/** Same hero band as the top (featured) invoice — Figma-style card. */
const DashboardInvoiceHeroBand: React.FC<{
  item: DashboardInvoiceActionItem;
  onViewAll?: () => void;
  /** Overdue: POST reminder. Otherwise opens billing (onViewAll). */
  onSendReminder?: () => void;
  /** This invoice: reminder request in flight */
  reminderSending?: boolean;
  /** Any invoice in the card: reminder in flight (blocks other overdue send buttons) */
  reminderAnyBusy?: boolean;
  statusToggle: boolean;
  statusUpdating?: boolean;
  /** Called when user picks a new status from the menu (same semantics as invoicing table). */
  onPickInvoiceStatus?: (next: InvoiceStatusSelectValue) => void;
  /** Reserve right padding so a floating collapse control does not cover amount/status */
  hasFloatingCollapse?: boolean;
}> = ({
  item,
  onViewAll,
  onSendReminder,
  reminderSending = false,
  reminderAnyBusy = false,
  statusToggle,
  statusUpdating,
  onPickInvoiceStatus,
  hasFloatingCollapse,
}) => {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const statusMenuOpen = Boolean(statusMenuAnchor);
  const overdue = item.status === 'overdue';
  const statusValue = normalizeDashboardSelectValue(item);
  const sendReminderDisabled = overdue && reminderAnyBusy;

  const statusButtonColor =
    statusValue === 'paid'
      ? '#28c76f'
      : statusValue === 'discarded'
        ? '#64748b'
        : overdue
          ? '#dc2626'
          : '#c2410c';

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      sx={{
        py: 1.5,
        pl: 1.75,
        pr: hasFloatingCollapse ? { xs: 4.5, sm: 5.5 } : 1.75,
        borderLeft: `2px solid ${CS_PRIMARY}99`,
        gap: { xs: 1.75, sm: 2.25 },
        alignItems: { sm: 'stretch' },
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            bgcolor: 'rgba(226,232,240,0.37)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PersonRoundedIcon sx={{ fontSize: 22, color: '#000' }} />
        </Box>
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 600,
                color: '#1e2939',
                pr: hasFloatingCollapse ? { xs: 4.5, sm: 0 } : 0,
                lineHeight: 1.45,
              }}
            >
              <ClientNameLink clientId={item.clientId} className="text-[#1e2939] hover:text-[#1e2939]">
                {item.clientName}
              </ClientNameLink>{' '}
              <Typography component="span" sx={{ fontWeight: 400, color: '#475569', fontSize: 'inherit' }}>
                · {item.invoiceNo || 'Invoice'}
              </Typography>
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0} sx={{ mt: 1, fontSize: '0.75rem', color: '#334155' }}>
              <ClockCircleIconRed />
              <Typography component="span" sx={{ ml: 1.25, fontSize: 'inherit', color: 'inherit' }}>
                {formatDashboardDueLabel(item.dueDate)}
              </Typography>
            </Stack>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={
              overdue && reminderSending ? (
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
              ) : (
                <NorthEastRoundedIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              if (overdue && onSendReminder) {
                void onSendReminder();
                return;
              }
              onViewAll?.();
            }}
            disabled={sendReminderDisabled}
            sx={{
              bgcolor: CS_PRIMARY,
              borderRadius: '10px',
              height: 34,
              maxWidth: 150,
              textTransform: 'none',
              '&:hover': { bgcolor: CS_PRIMARY, opacity: sendReminderDisabled ? 1 : 0.92 },
              ...(overdue && reminderSending
                ? {
                    '&.Mui-disabled': {
                      opacity: 1,
                      bgcolor: CS_PRIMARY,
                      color: '#fff',
                      WebkitTextFillColor: '#fff',
                    },
                  }
                : {}),
            }}
          >
            {overdue ? 'Send Reminder' : 'Review Invoice'}
          </Button>
        </Stack>
      </Stack>
      <Stack
        direction={{ xs: 'row', sm: 'column' }}
        alignItems={{ xs: 'center', sm: 'center' }}
        justifyContent={{ xs: 'space-between', sm: 'center' }}
        spacing={{ xs: 1, sm: 1.25 }}
        sx={{
          pt: { xs: 1.25, sm: 0 },
          borderTop: { xs: '1px solid rgba(0,0,0,0.06)', sm: 'none' },
          borderLeft: { sm: '1px solid rgba(0,0,0,0.07)' },
          pl: { sm: 2 },
          minWidth: { sm: 112 },
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.35 }}>
          {formatDashboardCurrency(item.totalDue, item.currency)}
        </Typography>
        {statusToggle && onPickInvoiceStatus ? (
          <>
            <Button
              variant="outlined"
              size="small"
              aria-expanded={statusMenuOpen}
              aria-haspopup="listbox"
              aria-label={`Invoice status for ${item.clientName}`}
              disabled={statusUpdating}
              endIcon={
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    transition: 'transform 0.2s ease',
                    transform: statusMenuOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                setStatusMenuAnchor(e.currentTarget);
              }}
              sx={{
                borderColor: 'rgba(0,0,0,0.07)',
                color: statusUpdating ? '#94a3b8' : statusButtonColor,
                fontWeight: statusValue === 'overdue' ? 700 : 600,
                borderRadius: 1,
                textTransform: 'none',
              }}
            >
              {statusUpdating ? 'Updating...' : statusSelectLabel(statusValue)}
            </Button>
            <Menu
              anchorEl={statusMenuAnchor}
              open={statusMenuOpen}
              onClose={() => setStatusMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { borderRadius: '10px', minWidth: 160, mt: 0.5, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)' },
                },
              }}
            >
              {DASHBOARD_INVOICE_STATUS_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt}
                  selected={opt === statusValue}
                  onClick={() => {
                    setStatusMenuAnchor(null);
                    onPickInvoiceStatus(opt);
                  }}
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: opt === statusValue ? 600 : 500,
                    color: statusSelectColor(opt),
                  }}
                >
                  {statusSelectLabel(opt)}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Tag
            variant="outlined"
            size="xs"
            className={chipClassNameForStatus(item.status === 'overdue')}
            sx={chipSxForStatus(item.status === 'overdue')}
          >
            {item.status === 'overdue' ? 'Overdue' : 'Due'}
          </Tag>
        )}
      </Stack>
    </Stack>
  );
};
const InvoiceRecommendedStrip: React.FC<{ text: string }> = ({ text }) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      bgcolor: '#f8fafc',
      borderTop: '1px solid rgba(148, 163, 184, 0.2)',
    }}
  >
    <Typography sx={{ fontSize: 12, color: '#334155', lineHeight: 1.55 }}>{text}</Typography>
  </Box>
);

/** One-line summary row (matches secondary invoices before expand). */
const CompactInvoiceSummaryRow: React.FC<{
  item: DashboardInvoiceActionItem;
  chevronOpen: boolean;
  onToggle: () => void;
  /** When true, row always looks "collapsed" (chevron down) and aria describes expanding the hero card */
  expandHeroMode?: boolean;
}> = ({ item, chevronOpen, onToggle, expandHeroMode }) => {
  const chipLabel = item.status === 'overdue' ? 'Overdue' : 'Due';
  const showChevronRotated = expandHeroMode ? false : chevronOpen;
  const ariaExpanded = expandHeroMode ? undefined : chevronOpen;
  const ariaLabel = expandHeroMode
    ? `Show full invoice card for ${item.clientName}`
    : `${chevronOpen ? 'Hide' : 'Show'} details for ${item.clientName}`;

  return (
    <Box
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      role="button"
      tabIndex={0}
      {...(ariaExpanded !== undefined ? { 'aria-expanded': ariaExpanded } : {})}
      aria-label={ariaLabel}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr) auto',
          sm: 'minmax(0, 1fr) 120px minmax(0, 1fr)',
        },
        gridTemplateAreas: {
          xs: '"client actions" "date actions"',
          sm: '"client date actions"',
        },
        columnGap: { xs: 1.5, sm: 2 },
        rowGap: 0.35,
        alignItems: 'center',
        px: 1.5,
        py: 1.15,
        cursor: 'pointer',
        outline: 'none',
        '&:focus-visible': { boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}33` },
        '&:hover': { bgcolor: 'rgba(248,250,252,0.6)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2.25} sx={{ gridArea: 'client', minWidth: 0 }}>
        <Box
          sx={{
            width: 27,
            height: 27,
            borderRadius: '7.7px',
            bgcolor: '#e8f5ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonRoundedIcon sx={{ fontSize: 16, color: CS_PRIMARY }} />
        </Box>
        <Stack spacing={0.375}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.8125rem', color: '#1a1a1a', lineHeight: 1.4 }}>
            <ClientNameLink clientId={item.clientId} className="text-[#1a1a1a] hover:text-[#1a1a1a]">
              {item.clientName}
            </ClientNameLink>
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#334155', lineHeight: 1.4 }}>{item.invoiceNo || toDashboardTitle(item.invoiceStatus) || 'Invoice'}</Typography>
        </Stack>
      </Stack>
      <Typography
        sx={{
          gridArea: 'date',
          fontSize: 14,
          color: '#334155',
          justifySelf: { xs: 'start', sm: 'center' },
          textAlign: { xs: 'left', sm: 'center' },
          lineHeight: 1.45,
          whiteSpace: 'nowrap',
        }}
      >
        {formatDashboardDateLabel(item.dueDate)}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ gridArea: 'actions', justifySelf: 'end' }}>
        <Typography sx={{ fontSize: 14, color: '#1e2939', fontWeight: 600, lineHeight: 1.45 }}>
          {formatDashboardCurrency(item.totalDue, item.currency)}
        </Typography>
        <Tag
          variant="outlined"
          size="xs"
          onClick={(e) => e.stopPropagation()}
          className={chipClassNameForStatus(chipLabel === 'Overdue')}
          sx={{
            height: 22,
            fontWeight: chipLabel === 'Overdue' ? 700 : 600,
            fontSize: 12,
            ...(chipLabel === 'Due'
              ? { bgcolor: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }
              : { bgcolor: '#fff1f2', color: '#dc2626', borderColor: '#fecdd3' }),
          }}
        >
          {chipLabel}
        </Tag>
        <KeyboardArrowDownRoundedIcon
          sx={{
            fontSize: 18,
            color: '#000',
            transition: 'transform 0.2s ease',
            transform: showChevronRotated ? 'rotate(180deg)' : 'none',
          }}
        />
      </Stack>
    </Box>
  );
};

export const DashboardInvoicesBillingCard: React.FC<DashboardInvoicesBillingCardProps> = ({
  items = [],
  isLoading = false,
  expanded,
  onToggleExpanded,
  onViewAll,
  onInvoiceDataChanged,
}) => {
  const [remindingInvoiceId, setRemindingInvoiceId] = useState<string | null>(null);
  const [expandedSecondaryInvoiceId, setExpandedSecondaryInvoiceId] = useState<string | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    item: DashboardInvoiceActionItem;
    next: InvoiceStatusSelectValue;
  } | null>(null);
  const [invoiceStatusFeedback, setInvoiceStatusFeedback] = useState<InvoiceDialogFeedback | null>(null);
  const [featuredHeroExpanded, setFeaturedHeroExpanded] = useState(true);
  const sortedItems = sortInvoiceItems(items);
  const featured = sortedItems[0];
  const compactRows = sortedItems.slice(1);
  const featuredRecommendedCopy = featured ? getHumanRecommendedAction(featured.recommendedAction) : null;
  const isEmpty = !isLoading && !featured;

  const handleSendReminder = useCallback(
    async (invoiceId: string) => {
      if (remindingInvoiceId) return;
      setRemindingInvoiceId(invoiceId);
      try {
        await resendInvoiceEmail(invoiceId);
        showSuccessToast('Reminder email sent');
        await onInvoiceDataChanged?.();
      } catch (error) {
        console.error('Failed to send invoice reminder', error);
        showErrorToast(extractErrorMessage(error) || 'Could not send reminder. Please try again.');
      } finally {
        setRemindingInvoiceId(null);
      }
    },
    [remindingInvoiceId, onInvoiceDataChanged]
  );

  const performStatusUpdate = useCallback(
    async (
      item: DashboardInvoiceActionItem,
      next: InvoiceStatusSelectValue,
      options?: { useInlineFeedback?: boolean },
    ) => {
      setStatusUpdatingId(item.id);
      try {
        await updateInvoiceStatus(
          item.id,
          next === 'paid'
            ? {
                status: 'paid',
                paidAt: new Date().toISOString(),
                paymentMethod: LIST_MARK_PAID_PAYMENT_METHOD,
              }
            : { status: mapUiStatusToApiStatus(next) }
        );
        showSuccessToast('Invoice status updated');
        setInvoiceStatusFeedback(null);
        setPendingStatusChange(null);
        await onInvoiceDataChanged?.();
      } catch (error) {
        console.error('Failed to update invoice status', error);
        if (options?.useInlineFeedback) {
          setInvoiceStatusFeedback({
            variant: 'error',
            title: 'Could not update invoice status',
            message: extractErrorMessage(error) || 'Could not update invoice status',
          });
        } else {
          showErrorToast(extractErrorMessage(error) || 'Could not update invoice status');
        }
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [onInvoiceDataChanged]
  );

  const handleInvoiceStatusPick = useCallback(
    (item: DashboardInvoiceActionItem, next: InvoiceStatusSelectValue) => {
      const current = normalizeDashboardSelectValue(item);
      if (next === current) return;
      if (next === 'paid' || next === 'discarded') {
        setInvoiceStatusFeedback(null);
        setPendingStatusChange({ item, next });
        return;
      }
      void performStatusUpdate(item, next);
    },
    [performStatusUpdate]
  );

  useEffect(() => {
    setFeaturedHeroExpanded(true);
    setExpandedSecondaryInvoiceId(null);
    setPendingStatusChange(null);
    setRemindingInvoiceId(null);
  }, [featured?.id]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: '1px solid #e8ecf0',
        px: 2.25,
        py: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        ...(isEmpty ? { minHeight: DASHBOARD_TODAY_CARD_EMPTY_MIN_HEIGHT * 0.72 } : {}),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 34, py: 0.25 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <InvoiceTaxDetailsIconBlue />
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', color: '#0f172a', letterSpacing: '-0.01em', lineHeight: 1.45 }}>
            Invoices and Billing
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button
            variant="text"
            size="small"
            onClick={onViewAll}
            endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: CS_PRIMARY,
              minWidth: 0,
            }}
          >
            View all
          </Button>
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
            onClick={onToggleExpanded}
            sx={{ color: '#64748b', '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)', color: '#0f172a' } }}
          >
            {expanded ? <ExpandLessRoundedIcon sx={{ fontSize: 20 }} /> : <ExpandMoreRoundedIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Stack>
      </Stack>

      {isLoading && !featured && (
        <Typography sx={{ fontSize: 12, color: '#4a5565', py: 0.5, lineHeight: 1.5 }}>Loading invoices...</Typography>
      )}

      {isEmpty && (
        <Box sx={DASHBOARD_TODAY_EMPTY_STATE_CONTAINER_SX}>
          <Typography sx={DASHBOARD_TODAY_EMPTY_STATE_TEXT_SX}>No invoices due right now.</Typography>
        </Box>
      )}

      {featured && !featuredHeroExpanded && (
        <Box
          sx={{
            border: '1px solid rgba(148, 163, 184, 0.22)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <CompactInvoiceSummaryRow
            item={featured}
            chevronOpen={false}
            expandHeroMode
            onToggle={() => {
              setFeaturedHeroExpanded(true);
            }}
          />
        </Box>
      )}

      {featured && featuredHeroExpanded && (
        <Paper elevation={0} sx={{ ...invoiceHeroPaperSx, position: 'relative' }}>
          <IconButton
            size="small"
            aria-label="Collapse invoice summary"
            onClick={() => {
              setFeaturedHeroExpanded(false);
            }}
            sx={heroCollapseIconSx}
          >
            <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <DashboardInvoiceHeroBand
            item={featured}
            onViewAll={onViewAll}
            onSendReminder={() => void handleSendReminder(featured.id)}
            reminderSending={remindingInvoiceId === featured.id}
            reminderAnyBusy={remindingInvoiceId !== null}
            statusToggle
            statusUpdating={statusUpdatingId === featured.id}
            onPickInvoiceStatus={(next) => handleInvoiceStatusPick(featured, next)}
            hasFloatingCollapse
          />
          {featuredRecommendedCopy ? <InvoiceRecommendedStrip text={featuredRecommendedCopy} /> : null}
        </Paper>
      )}

      {expanded && featured && (
        <>
          <Stack spacing={1}>
            {compactRows.map((row) => {
              const rowOpen = expandedSecondaryInvoiceId === row.id;
              const recommendedCopy = getHumanRecommendedAction(row.recommendedAction);
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: '1px solid rgba(148, 163, 184, 0.22)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}
                >
                  {rowOpen ? (
                    <Paper elevation={0} sx={{ ...invoiceHeroPaperSx, position: 'relative' }}>
                      <IconButton
                        size="small"
                        aria-label="Collapse to compact row"
                        onClick={() => {
                          setExpandedSecondaryInvoiceId(null);
                        }}
                        sx={heroCollapseIconSx}
                      >
                        <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <DashboardInvoiceHeroBand
                        item={row}
                        onViewAll={onViewAll}
                        onSendReminder={() => void handleSendReminder(row.id)}
                        reminderSending={remindingInvoiceId === row.id}
                        reminderAnyBusy={remindingInvoiceId !== null}
                        statusToggle
                        statusUpdating={statusUpdatingId === row.id}
                        onPickInvoiceStatus={(next) => handleInvoiceStatusPick(row, next)}
                        hasFloatingCollapse
                      />
                      {recommendedCopy ? <InvoiceRecommendedStrip text={recommendedCopy} /> : null}
                    </Paper>
                  ) : (
                    <CompactInvoiceSummaryRow
                      item={row}
                      chevronOpen={false}
                      onToggle={() => {
                        setExpandedSecondaryInvoiceId(row.id);
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </>
      )}

      <InvoiceStatusConfirmDialog
        open={Boolean(pendingStatusChange)}
        targetStatus={pendingStatusChange?.next === 'paid' ? 'paid' : pendingStatusChange?.next === 'discarded' ? 'discarded' : null}
        isSubmitting={Boolean(statusUpdatingId)}
        feedback={invoiceStatusFeedback}
        onCancel={() => {
          setInvoiceStatusFeedback(null);
          setPendingStatusChange(null);
        }}
        onConfirm={() => {
          if (pendingStatusChange) {
            void performStatusUpdate(pendingStatusChange.item, pendingStatusChange.next, { useInlineFeedback: true });
          }
        }}
      />
    </Paper>
  );
};
